import { BACKEND_URL } from '../config/backendUrl';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { recipientsApi, documentsApi, getErrorMessage } from '../services/api';
import axios from 'axios';
import { Button } from '../components/ui/button';
import ListPagination, { LIST_PAGE_SIZE } from '../components/ui/ListPagination';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  Search,
  RefreshCw,
  User,
  Building2,
  Mail,
  Clock,
  CheckCircle2,
  Bell,
  ChevronDown,
  ChevronUp,
  FileText,
  Eye,
  XCircle,
  Shield,
  ShieldCheck,
  FileCheck,
  AlertCircle,
  X,
  Ban,
  Trash2,
  Send,
  KeyRound,
  Copy
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

const API_URL = BACKEND_URL;

// Bullhorn entity colors (with safe fallback so unexpected entity types
// like 'placement' don't crash the row renderer)
const ENTITY_COLORS = {
  candidate: { bg: 'bg-[#4CAF50]', text: 'text-white', label: 'Candidate', lightBg: 'bg-[#4CAF50]/10', borderColor: 'border-[#4CAF50]', iconColor: 'text-[#4CAF50]' },
  contact: { bg: 'bg-[#F9A825]', text: 'text-white', label: 'Contact', lightBg: 'bg-[#F9A825]/10', borderColor: 'border-[#F9A825]', iconColor: 'text-[#F9A825]' },
  placement: { bg: 'bg-[#1a4a5e]', text: 'text-white', label: 'Placement', lightBg: 'bg-[#1a4a5e]/10', borderColor: 'border-[#1a4a5e]', iconColor: 'text-[#1a4a5e]' },
};
const FALLBACK_ENTITY_COLOR = { bg: 'bg-slate-400', text: 'text-white', label: 'Recipient', lightBg: 'bg-slate-100', borderColor: 'border-slate-300', iconColor: 'text-slate-600' };

// Status badge component
const StatusBadge = ({ status, size = 'sm' }) => {
  const config = {
    sent: { class: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Sent', icon: Clock },
    opened: { class: 'bg-cyan-100 text-cyan-700 border-cyan-200', label: 'Opened', icon: Eye },
    in_progress: { class: 'bg-purple-100 text-purple-700 border-purple-200', label: 'In Progress', icon: RefreshCw },
    completed: { class: 'bg-green-100 text-green-700 border-green-200', label: 'Completed', icon: CheckCircle2 },
    pending_approval: { class: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Pending Approval', icon: FileCheck },
    approved: { class: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: 'Approved', icon: Shield },
    expired: { class: 'bg-red-100 text-red-700 border-red-200', label: 'Expired', icon: XCircle },
    // TrustID verification statuses (compliance/recipient-detail view only —
    // the candidate portal never shows the literal pass/fail/referred outcome).
    pending_start: { class: 'bg-slate-100 text-slate-700 border-slate-200', label: 'Not Started', icon: Clock },
    link_created: { class: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Link Sent', icon: Send },
    submitted: { class: 'bg-purple-100 text-purple-700 border-purple-200', label: 'Submitted', icon: RefreshCw },
    pass: { class: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: 'Passed', icon: CheckCircle2 },
    fail: { class: 'bg-red-100 text-red-700 border-red-200', label: 'Failed', icon: XCircle },
    referred: { class: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Referred', icon: AlertCircle },
    cancelled: { class: 'bg-slate-100 text-slate-500 border-slate-200', label: 'Cancelled', icon: Ban },
    // Credas verification statuses (raw values from credas_candidates.status).
    complete: { class: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: 'Complete', icon: CheckCircle2 },
    failed: { class: 'bg-red-100 text-red-700 border-red-200', label: 'Failed', icon: XCircle },
    credas_failed: { class: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Link Error', icon: AlertCircle },
  };
  
  const { class: className, label, icon: Icon } = config[status] || config.sent;
  const sizeClass = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm';
  
  return (
    <span className={`inline-flex items-center gap-1 rounded-full font-medium border ${className} ${sizeClass}`}>
      <Icon className={size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} />
      {label}
    </span>
  );
};

// The actual pass/fail outcome of a Credas identity check — distinct from
// v.status (which only says the process finished, not whether the candidate
// passed). Only ever set once status is 'completed'. Never shown on the
// candidate portal (see credas.repo.ts's toCredasListResp vs
// toCredasPortalResponse — the portal response has no such field at all).
const VERIFICATION_RESULT_BADGE = {
  pass: { class: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: 'Pass' },
  fail: { class: 'bg-red-100 text-red-700 border-red-200', label: 'Fail' },
  refer: { class: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Refer' },
  pending: { class: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Pending Review' },
};
// Mirrors BullhornEmbedPage.js's credasOutcomeBadge: the real result once it
// exists, "Awaiting Results" while it doesn't, "Cancelled" (not "Fail") for
// a withdrawn check, and nothing for credas_failed (the StatusBadge right
// next to this already says "Link Error" — a send failure, not an outcome).
const VerificationResultBadge = ({ status, result }) => {
  if (status === 'cancelled') {
    return (
      <span className="inline-flex items-center rounded-full font-medium border px-2 py-0.5 text-xs bg-slate-100 text-slate-500 border-slate-200">
        Cancelled
      </span>
    );
  }
  if (status === 'credas_failed') return null;
  const config = VERIFICATION_RESULT_BADGE[result] || { class: 'bg-slate-100 text-slate-600 border-slate-200', label: 'Awaiting Results' };
  return (
    <span
      className={`inline-flex items-center rounded-full font-medium border px-2 py-0.5 text-xs ${config.class}`}
      title="Identity check outcome — distinct from the process status"
    >
      {config.label}
    </span>
  );
};

// Progress indicator component
const ProgressIndicator = ({ completed, total, label }) => {
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
  let colorClass = 'bg-red-500';
  if (percentage >= 100) colorClass = 'bg-emerald-500';
  else if (percentage >= 50) colorClass = 'bg-amber-500';
  else if (percentage > 0) colorClass = 'bg-orange-500';

  return (
    <div className="flex items-center gap-3">
      <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${colorClass}`} style={{ width: `${percentage}%` }} />
      </div>
      <span className="text-sm font-medium min-w-[60px]">{completed}/{total}</span>
    </div>
  );
};

// Expandable row for document details
const RecipientDocumentsPanel = ({ recipient, onSendReminder, sendingReminder, onDocumentCancelled }) => {
  const [cancellingDoc, setCancellingDoc] = useState(null);
  const [previewLoadingId, setPreviewLoadingId] = useState(null);

  const outstandingDocs = recipient.documents?.filter(d => !['completed', 'approved', 'cancelled'].includes(d.status)) || [];
  const completedDocs = recipient.documents?.filter(d => ['completed', 'approved'].includes(d.status)) || [];

  const handleCancelDocument = async (docId, templateName) => {
    if (!window.confirm(`Cancel "${templateName}" for ${recipient.name}? This will remove it from their portal.`)) {
      return;
    }

    setCancellingDoc(docId);
    try {
      await documentsApi.cancel(docId);
      toast.success('Document cancelled');
      onDocumentCancelled?.();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to cancel document'));
    } finally {
      setCancellingDoc(null);
    }
  };

  // A completed document has its own final signed PDF. An outstanding one
  // doesn't — GET .../preview-pdf renders the template with whatever values
  // are on it right now (e.g. prefilled from Bullhorn at send time)
  // overlaid, same mechanism BullhornEmbedPage.js's preview already uses.
  // Falling back straight to template_file_url (the previous behavior here)
  // shows the raw, unfilled template, which looks like "the preview is
  // blank" for any document the candidate hasn't completed yet even when
  // send-time prefill worked correctly.
  const handlePreview = async (doc) => {
    const baseUrl = BACKEND_URL;
    if (doc.completed_document_url) {
      setPreviewDoc({ ...doc, _pdfUrl: `${baseUrl}${doc.completed_document_url}` });
      return;
    }
    setPreviewLoadingId(doc.id);
    try {
      const { data } = await documentsApi.getPreviewPdf(doc.id);
      if (data?.preview_url) {
        setPreviewDoc({ ...doc, _pdfUrl: `${baseUrl}${data.preview_url}` });
        return;
      }
    } catch (error) {
      // fall through to the raw template below as a last resort
    } finally {
      setPreviewLoadingId(null);
    }
    if (doc.template_file_url) {
      setPreviewDoc({ ...doc, _pdfUrl: `${baseUrl}${doc.template_file_url}` });
    } else {
      toast.info('No PDF available for preview');
    }
  };

  const [previewDoc, setPreviewDoc] = useState(null);

  return (
    <div className="bg-slate-50 border-t border-border p-6 animate-in slide-in-from-top-2 duration-200">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Outstanding Documents */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-4 h-4 text-amber-600" />
            <h4 className="font-semibold text-amber-900">Outstanding ({outstandingDocs.length})</h4>
          </div>
          {outstandingDocs.length > 0 ? (
            <div className="space-y-2">
              {outstandingDocs.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200 shadow-sm">
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-amber-600" />
                    <div>
                      <p className="font-medium text-sm">{doc.template_name}</p>
                      <p className="text-xs text-muted-foreground">
                        Sent {doc.sent_at ? format(new Date(doc.sent_at), 'MMM d, yyyy') : 'N/A'}
                        {doc.reminder_count > 0 && <span className="text-amber-600"> • {doc.reminder_count} reminder(s)</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={doc.status} />
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-slate-500 hover:text-primary hover:bg-primary/10"
                      onClick={() => handlePreview(doc)}
                      disabled={previewLoadingId === doc.id}
                      title="Preview document"
                      data-testid={`preview-doc-${doc.id}`}
                    >
                      {previewLoadingId === doc.id ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Eye className="w-3.5 h-3.5" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleCancelDocument(doc.id, doc.template_name)}
                      disabled={cancellingDoc === doc.id}
                      title="Cancel document"
                    >
                      {cancellingDoc === doc.id ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <X className="w-3.5 h-3.5" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic p-3 bg-white rounded-lg border border-dashed">No outstanding documents</p>
          )}
        </div>

        {/* Completed Documents */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <h4 className="font-semibold text-emerald-900">Completed ({completedDocs.length})</h4>
          </div>
          {completedDocs.length > 0 ? (
            <div className="space-y-2">
              {completedDocs.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-emerald-200 shadow-sm">
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-emerald-600" />
                    <div>
                      <p className="font-medium text-sm">{doc.template_name}</p>
                      <p className="text-xs text-muted-foreground">
                        Completed {doc.completed_at ? format(new Date(doc.completed_at), 'MMM d, yyyy') : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-slate-500 hover:text-primary hover:bg-primary/10"
                      onClick={() => handlePreview(doc)}
                      title="Preview document"
                      data-testid={`preview-completed-${doc.id}`}
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                    <StatusBadge status={doc.status} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic p-3 bg-white rounded-lg border border-dashed">No completed documents yet</p>
          )}
        </div>
      </div>

      {/* Identity Verification (Credas/TrustID) — compliance view, shows the
          literal pass/fail/referred outcome (the candidate portal never does). */}
      {recipient.verifications?.length > 0 && (
        <div className="mt-6 pt-6 border-t border-border">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-4 h-4 text-slate-600" />
            <h4 className="font-semibold text-slate-900">Identity Verification</h4>
          </div>
          <div className="space-y-2">
            {recipient.verifications.map((v) => (
              <div key={v.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200 shadow-sm" data-testid={`recipient-verification-${v.id}`}>
                <div className="flex items-center gap-3">
                  <Shield className="w-4 h-4 text-slate-500" />
                  <div>
                    <p className="font-medium text-sm">
                      {v.name || (v.check_type === 'right_to_work' ? 'Right to Work Check' : v.check_type)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {v.rtw_outcome ? `${v.rtw_outcome}` : 'No result yet'}
                      {v.rtw_follow_up_date && ` · Expires ${format(new Date(v.rtw_follow_up_date), 'MMM d, yyyy')}`}
                      {v.needs_review && <span className="text-amber-600"> · Needs review</span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {v.provider === 'credas' && <VerificationResultBadge status={v.status} result={v.verification_result} />}
                  <StatusBadge status={v.status} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Send Reminder Action */}
      {outstandingDocs.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <Button 
            onClick={() => onSendReminder(recipient)}
            disabled={sendingReminder === recipient.email}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {sendingReminder === recipient.email ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Bell className="w-4 h-4 mr-2" />
            )}
            Send Reminder for {outstandingDocs.length} Outstanding Document{outstandingDocs.length > 1 ? 's' : ''}
          </Button>
        </div>
      )}

      {/* Document Preview Modal */}
      <Dialog open={!!previewDoc} onOpenChange={(open) => { if (!open) setPreviewDoc(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0" aria-describedby="recipient-preview-desc">
          <DialogHeader className="px-6 py-4 border-b flex-shrink-0">
            <DialogTitle className="text-lg font-bold">{previewDoc?.template_name}</DialogTitle>
            <p className="text-sm text-muted-foreground" id="recipient-preview-desc">
              {recipient.name} • {recipient.email}
            </p>
          </DialogHeader>
          <div className="flex-1 overflow-auto bg-slate-100">
            {previewDoc?._pdfUrl && (
              <iframe
                src={previewDoc._pdfUrl}
                title="Document Preview"
                className="w-full border-0"
                style={{ height: 'calc(85vh - 80px)', minHeight: '500px' }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default function RecipientsPage({ embedded = false }) {
  const navigate = useNavigate();
  const { getTenantPath, tenant } = useAuth();
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [entityFilter, setEntityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedRow, setExpandedRow] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [resendingLogin, setResendingLogin] = useState(null);
  const [sendingCredas, setSendingCredas] = useState(null);
  const [credasEnabled, setCredasEnabled] = useState(false);
  const [sendingTrustid, setSendingTrustid] = useState(null);
  const [trustidEnabled, setTrustidEnabled] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    loadRecipients();
  }, [entityFilter, statusFilter, page]);

  // Fetched once here rather than per-row (the old IdVerificationAction
  // component fetched this inside every expanded recipient's own effect,
  // meaning N parallel identical requests for N expanded rows).
  useEffect(() => {
    let alive = true;
    axios.get(`${API_URL}/api/credas/config`)
      .then(({ data }) => { if (alive) setCredasEnabled(!!data?.enabled && !!data?.api_key_set); })
      .catch(() => { if (alive) setCredasEnabled(false); });
    axios.get(`${API_URL}/api/trustid/config`)
      .then(({ data }) => { if (alive) setTrustidEnabled(!!data?.enabled && !!data?.password_set); })
      .catch(() => { if (alive) setTrustidEnabled(false); });
    return () => { alive = false; };
  }, []);

  const loadRecipients = async () => {
    setLoading(true);
    try {
      const response = await recipientsApi.list({
        search: search || undefined,
        entity_type: entityFilter !== 'all' ? entityFilter : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        page,
        limit: LIST_PAGE_SIZE
      });
      setRecipients(response.data.recipients || []);
      setTotalPages(response.data.total_pages || 1);
      setTotalCount(response.data.total_count || response.data.recipients?.length || 0);
    } catch (error) {
      toast.error('Failed to load recipients');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    setExpandedRow(null);
    loadRecipients();
  };

  const handleClearSearch = () => {
    setSearch('');
    setPage(1);
    setExpandedRow(null);
    loadRecipients();
  };

  const handleSendReminder = async (recipient) => {
    setSendingReminder(recipient.email);
    try {
      await recipientsApi.sendReminder({
        recipient_email: recipient.email
      });
      toast.success(`Reminder sent to ${recipient.name}`);
      loadRecipients();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send reminder'));
    } finally {
      setSendingReminder(null);
    }
  };

  const handleResendLogin = async (recipient) => {
    setResendingLogin(recipient.email);
    try {
      await axios.post(`${API_URL}/api/portal/auth/resend-credentials`, {
        email: recipient.email,
        tenant_id: tenant?.id,
      });
      toast.success(`New login details sent to ${recipient.email}`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to resend login details'));
    } finally {
      setResendingLogin(null);
    }
  };

  const handleSendCredas = async (recipient) => {
    setSendingCredas(recipient.email);
    try {
      const parts = (recipient.name || '').trim().split(/\s+/);
      const { data } = await axios.post(`${API_URL}/api/credas/initiate`, {
        candidate_email: recipient.email,
        first_name: parts[0] || '',
        last_name: parts.length > 1 ? parts.slice(1).join(' ') : '',
      });
      if (data?.status === 'credas_failed') {
        toast.error(`Credas failed: ${data.last_error || 'unknown error'}`);
      } else {
        toast.success('Credas check sent');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not start Credas check'));
    } finally {
      setSendingCredas(null);
    }
  };

  const handleRunTrustid = async (recipient) => {
    setSendingTrustid(recipient.email);
    try {
      const parts = (recipient.name || '').trim().split(/\s+/);
      const { data } = await axios.post(`${API_URL}/api/trustid/initiate`, {
        candidate_email: recipient.email,
        candidate_name: recipient.name || '',
        first_name: parts[0] || '',
        last_name: parts.length > 1 ? parts.slice(1).join(' ') : '',
      });
      if (data?.status === 'credas_failed') {
        toast.error(`Right to Work check failed: ${data.last_error || 'unknown error'}`);
      } else {
        toast.success('Right to Work check sent');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not start Right to Work check'));
    } finally {
      setSendingTrustid(null);
    }
  };

  // Cache of detailed recipient data (documents array) keyed by email
  const [recipientDetails, setRecipientDetails] = useState({});

  const loadRecipientDetail = async (email) => {
    try {
      const { data } = await recipientsApi.get(email);
      setRecipientDetails((prev) => ({
        ...prev,
        [email]: {
          documents: [
            ...(data.outstanding_documents || []),
            ...(data.completed_documents || []),
          ],
          verifications: data.verifications || [],
        },
      }));
    } catch (err) {
      // Silent fail — panel just shows "no documents"; toast would be too noisy
    }
  };

  const toggleRow = async (email) => {
    const next = expandedRow === email ? null : email;
    setExpandedRow(next);
    // When expanding, fetch the per-recipient document list (the listing
    // aggregation only returns counts, not the docs themselves).
    if (next && !recipientDetails[email]) {
      await loadRecipientDetail(email);
    }
  };

  // Calculate summary stats from current page (using the API's actual field names)
  const completeCount = recipients.filter(
    (r) => (r.pending_documents || 0) === 0 && (r.total_documents || 0) > 0
  ).length;
  const pendingCount = recipients.filter((r) => (r.pending_documents || 0) > 0).length;

  const content = (
    <div className="space-y-6" data-testid="recipients-page">
      {/* Header - only show when not embedded */}
      {!embedded && (
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold">Recipients</h1>
            <p className="text-muted-foreground">Track document progress for each candidate and contact</p>
          </div>
          
          {/* Summary Stats */}
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => navigate(getTenantPath('/send'))} 
              className="btn-primary"
              data-testid="send-document-from-recipients"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Document
            </Button>
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 border border-slate-200">
              <User className="w-4 h-4 text-slate-600" />
              <span className="text-sm font-semibold text-slate-700">{totalCount} Total</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-50 border border-emerald-200">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-semibold text-emerald-700">{completeCount} Complete</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-50 border border-amber-200">
              <Clock className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-semibold text-amber-700">{pendingCount} Pending</span>
            </div>
          </div>
        </div>
      )}

      {/* Summary Stats when embedded */}
      {embedded && (
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 border border-slate-200">
              <User className="w-4 h-4 text-slate-600" />
              <span className="text-sm font-semibold text-slate-700">{totalCount} Total</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-semibold text-emerald-700">{completeCount} Complete</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200">
              <Clock className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-semibold text-amber-700">{pendingCount} Pending</span>
            </div>
          </div>
          <Button 
            onClick={() => navigate(getTenantPath('/send'))} 
            className="btn-primary"
            data-testid="send-document-from-recipients"
          >
            <Send className="w-4 h-4 mr-2" />
            Send Document
          </Button>
        </div>
      )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 p-4 bg-white rounded-xl border border-border shadow-sm">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search by name or email..."
              className="pl-10 pr-10"
              data-testid="search-input"
            />
            {search && (
              <button 
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Select value={entityFilter} onValueChange={(v) => { setEntityFilter(v); setPage(1); setExpandedRow(null); }}>
            <SelectTrigger className="w-[160px]" data-testid="entity-filter">
              <SelectValue placeholder="Entity Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="candidate">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#4CAF50]"></span>
                  Candidates
                </span>
              </SelectItem>
              <SelectItem value="contact">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#F9A825]"></span>
                  Contacts
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); setExpandedRow(null); }}>
            <SelectTrigger className="w-[160px]" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                  Pending
                </span>
              </SelectItem>
              <SelectItem value="complete">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  Complete
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleSearch} disabled={loading} className="btn-primary">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
            Search
          </Button>
        </div>

        {/* Recipients Table */}
        <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : recipients.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="w-12"></TableHead>
                  <TableHead className="font-semibold">Recipient</TableHead>
                  <TableHead className="font-semibold">Type</TableHead>
                  <TableHead className="font-semibold">Documents Sent</TableHead>
                  <TableHead className="font-semibold">Outstanding</TableHead>
                  <TableHead className="font-semibold">Completed</TableHead>
                  <TableHead className="font-semibold">Last Activity</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recipients.map((recipient) => {
                  const entityType = recipient.entity_type || 'candidate';
                  const entityColor = ENTITY_COLORS[entityType] || FALLBACK_ENTITY_COLOR;
                  const totalCount = recipient.total_documents || 0;
                  const completedCount = recipient.completed_documents || 0;
                  const outstandingCount = recipient.pending_documents ?? (totalCount - completedCount);
                  const isComplete = totalCount > 0 && outstandingCount === 0;
                  const isExpanded = expandedRow === recipient.email;
                  // Hydrate the detail list once it's been fetched
                  const recipientWithDocs = {
                    ...recipient,
                    documents: recipientDetails[recipient.email]?.documents || [],
                    verifications: recipientDetails[recipient.email]?.verifications || [],
                  };
                  
                  return (
                    <>
                      <TableRow 
                        key={recipient.email}
                        className={`cursor-pointer transition-colors ${isExpanded ? 'bg-slate-50' : 'hover:bg-slate-50/50'} ${isComplete ? 'bg-emerald-50/30' : ''}`}
                        onClick={() => toggleRow(recipient.email)}
                        data-testid={`recipient-row-${recipient.email}`}
                      >
                        <TableCell className="text-center">
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-muted-foreground mx-auto" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-muted-foreground mx-auto" />
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full ${entityColor.lightBg} flex items-center justify-center flex-shrink-0`}>
                              {entityType === 'contact' ? (
                                <Building2 className={`w-5 h-5 ${entityColor.iconColor}`} />
                              ) : (
                                <User className={`w-5 h-5 ${entityColor.iconColor}`} />
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium truncate">{recipient.name}</p>
                              <p className="text-sm text-muted-foreground truncate flex items-center gap-1">
                                <Mail className="w-3 h-3 flex-shrink-0" />
                                {recipient.email}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${entityColor.bg} ${entityColor.text}`}>
                            {entityColor.label}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="font-semibold text-lg">{recipient.total_documents}</span>
                        </TableCell>
                        <TableCell>
                          {outstandingCount > 0 ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                              <AlertCircle className="w-3.5 h-3.5" />
                              {outstandingCount}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              None
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <ProgressIndicator 
                            completed={completedCount} 
                            total={totalCount} 
                          />
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {recipient.last_activity 
                              ? formatDistanceToNow(new Date(recipient.last_activity), { addSuffix: true })
                              : '-'
                            }
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(getTenantPath(`/send/single?name=${encodeURIComponent(recipient.name)}&email=${encodeURIComponent(recipient.email)}&type=${entityType}`));
                              }}
                              className="border-primary/30 text-primary hover:bg-primary/5"
                              title="Send document to this recipient"
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                            {credasEnabled && entityType === 'candidate' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSendCredas(recipient);
                                }}
                                disabled={sendingCredas === recipient.email}
                                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                                title="Send to Credas"
                                data-testid={`send-credas-${recipient.email}`}
                              >
                                {sendingCredas === recipient.email ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <ShieldCheck className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                            {trustidEnabled && entityType === 'candidate' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRunTrustid(recipient);
                                }}
                                disabled={sendingTrustid === recipient.email}
                                className="border-sky-300 text-sky-700 hover:bg-sky-50"
                                title="Run Right to Work Check"
                                data-testid={`send-trustid-${recipient.email}`}
                              >
                                {sendingTrustid === recipient.email ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <ShieldCheck className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                            {entityType === 'candidate' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleResendLogin(recipient);
                                }}
                                disabled={resendingLogin === recipient.email}
                                className="border-orange-300 text-orange-700 hover:bg-orange-50"
                                title="Resend portal login details"
                                data-testid={`resend-login-${recipient.email}`}
                              >
                                {resendingLogin === recipient.email ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <KeyRound className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                            {outstandingCount > 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSendReminder(recipient);
                                }}
                                disabled={sendingReminder === recipient.email}
                                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                                title="Send reminder"
                              >
                                {sendingReminder === recipient.email ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Bell className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow key={`${recipient.email}-details`}>
                          <TableCell colSpan={8} className="p-0">
                            <RecipientDocumentsPanel 
                              recipient={recipientWithDocs} 
                              onSendReminder={handleSendReminder}
                              sendingReminder={sendingReminder}
                              onDocumentCancelled={() => {
                                setRecipientDetails((prev) => {
                                  const next = { ...prev };
                                  delete next[recipient.email];
                                  return next;
                                });
                                loadRecipients();
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-16">
              <User className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium mb-2">No recipients found</h3>
              <p className="text-muted-foreground">
                {search || entityFilter !== 'all' || statusFilter !== 'all' 
                  ? 'Try adjusting your search or filters' 
                  : 'Send documents to start tracking progress'
                }
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        <ListPagination
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          noun="recipients"
          onPageChange={(p) => { setPage(p); setExpandedRow(null); }}
        />
      </div>
  );

  // If embedded, return content directly without DashboardLayout
  if (embedded) {
    return content;
  }

  return (
    <DashboardLayout>
      {content}
    </DashboardLayout>
  );
}
